import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconHeart, IconHeartFilled } from '@tabler/icons-react';

import { formatCurrency } from '../../helpers/formatters';
import { useObservability } from '../../helpers/observability';

/**
 * Structural link wrapping a region of the card. Atoms `Link` is for inline
 * text links (it underlines and recolors), so container links use a layout
 * primitive with `href` instead and track clicks via `onClick` since
 * containers have no built-in click analytics.
 */
const CardLink: React.FC<{
  href?: string | null;
  ariaLabel: string;
  analyticsName: string;
  className?: string;
  children: React.ReactNode;
}> = ({ href, ariaLabel, analyticsName, className, children }) => {
  const { trackJourneyEvent } = useObservability();

  const handleClick = useCallback(() => {
    trackJourneyEvent('platform', 'linkClickEvent', { targetName: analyticsName });
  }, [trackJourneyEvent, analyticsName]);

  if (href == null) {
    return <Col className={className}>{children}</Col>;
  }

  return (
    <Col href={href} aria-label={ariaLabel} onClick={handleClick} className={className}>
      {children}
    </Col>
  );
};

export type PropertyCardProps = {
  beds?: number | null;
  baths?: number | null;
  city?: string | null;
  dwellingType?: string | null;
  estimatedMonthlyPayment?: number | null;
  href?: string | null;
  isFavorite?: boolean | null;
  neighborhood?: string | null;
  onFavoritePress?: () => void;
  photoUrl?: string | null;
  /**
   * Rendered inside the photo container, on top of the photo — badges, hover
   * treatments, etc. Position children with `absolute`; the container is
   * `relative` and clips to the photo's rounded bounds, so consumers never
   * have to mirror the card's aspect ratio or radius.
   */
  photoOverlay?: React.ReactNode;
  photoFetchPriority?: React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
  price?: number | null;
  sqft?: number | null;
};

const PROPERTY_CARD_IMAGE_CLASS = 'aspect-[16/9] w-full rounded-2xl object-cover';

const PropertyCardImage: React.FC<{
  photoUrl?: string | null;
  photoFetchPriority?: React.ImgHTMLAttributes<HTMLImageElement>['fetchPriority'];
}> = ({ photoUrl, photoFetchPriority }) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [isPhotoLoaded, setIsPhotoLoaded] = useState(false);

  useLayoutEffect(() => {
    if (!photoUrl) {
      setIsPhotoLoaded(false);
      return;
    }

    setIsPhotoLoaded(false);
    const image = imageRef.current;
    if (image?.complete && image.naturalWidth > 0) {
      setIsPhotoLoaded(true);
    }
  }, [photoUrl]);

  if (!photoUrl) {
    return (
      <Box aria-hidden className={cn('skeleton bg-od-bg-tertiary', PROPERTY_CARD_IMAGE_CLASS)} />
    );
  }

  return (
    <>
      <Image
        ref={imageRef}
        alt="Main view of property"
        src={photoUrl}
        loading="lazy"
        fetchPriority={photoFetchPriority}
        onLoad={() => setIsPhotoLoaded(true)}
        onError={() => setIsPhotoLoaded(true)}
        className={PROPERTY_CARD_IMAGE_CLASS}
      />
      {!isPhotoLoaded && (
        <Box aria-hidden className="skeleton absolute inset-0 rounded-2xl bg-od-bg-tertiary" />
      )}
    </>
  );
};

export const PropertyCard: React.FC<PropertyCardProps> = ({
  beds,
  baths,
  city,
  dwellingType,
  estimatedMonthlyPayment,
  href,
  isFavorite,
  neighborhood,
  onFavoritePress,
  photoUrl,
  photoFetchPriority,
  photoOverlay,
  price,
  sqft,
}) => {
  return (
    <Col className="w-full gap-5">
      <CardLink
        href={href}
        ariaLabel="Main view of property"
        analyticsName="marketplace-property-card-image-link"
        className="relative w-full overflow-hidden rounded-2xl"
      >
        <PropertyCardImage photoUrl={photoUrl} photoFetchPriority={photoFetchPriority} />
        {photoOverlay}
      </CardLink>
      <Col className="gap-3">
        <Row className="items-center justify-between">
          <Row className="items-baseline gap-1">
            {price != null && (
              <Text as="span" className="text-label-xl text-od-content-primary">
                {formatCurrency(price, { inDollars: true })}
              </Text>
            )}
            {estimatedMonthlyPayment != null && (
              <Text as="span" className="text-body-sm text-od-content-tertiary">
                {formatCurrency(estimatedMonthlyPayment, { inDollars: true })}/month est.
              </Text>
            )}
          </Row>
          {onFavoritePress && (
            <Row className="justify-end self-center">
              <Button
                variant="secondary"
                aria-label={isFavorite ? 'unfavorite' : 'favorite'}
                analyticsName="marketplace-property-card-favorite-button"
                onClick={onFavoritePress}
                className="size-6 rounded-full border-0 bg-transparent p-0"
              >
                {isFavorite ? (
                  <IconHeartFilled size={20} className="text-od-content-error" />
                ) : (
                  <IconHeart size={20} />
                )}
              </Button>
            </Row>
          )}
        </Row>
        <CardLink
          href={href}
          ariaLabel="Property details"
          analyticsName="marketplace-property-card-info-link"
          className="w-full"
        >
          <Row className="w-full items-baseline whitespace-nowrap">
            <Text as="span" className="text-body-sm text-od-content-tertiary">
              {beds != null && `${beds} bds`}
            </Text>
            {baths != null && (
              <>
                <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                  {' • '}
                </Text>
                <Text as="span" className="text-body-sm text-od-content-tertiary">
                  {baths} ba
                </Text>
              </>
            )}
            {sqft != null && (
              <>
                <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                  {' • '}
                </Text>
                <Text as="span" className="text-body-sm text-od-content-tertiary">
                  {sqft.toLocaleString()} sqft
                </Text>
              </>
            )}
            {dwellingType && (
              <>
                <Text as="span" className="text-body-sm text-od-content-inactive-secondary">
                  {' • '}
                </Text>
                <Text as="span" className="text-body-sm text-od-content-tertiary">
                  {dwellingType}
                </Text>
              </>
            )}
          </Row>
          <Row className="items-baseline gap-0.5">
            {neighborhood && (
              <Text as="span" className="text-body-sm text-od-content-tertiary">
                {city ? `${neighborhood}, ` : neighborhood}
              </Text>
            )}
            {city && (
              <Text as="span" className="text-body-sm text-od-content-tertiary">
                {city}
              </Text>
            )}
          </Row>
        </CardLink>
      </Col>
    </Col>
  );
};
